import React, {
  type CSSProperties,
  useRef,
  useState,
  useLayoutEffect,
} from "react";
import "./Select.css";
import { useClickOutside } from "../../utils/OutsideClick";
import Input from "../reusable-input/Input";
import Checkbox from "../reusable-checkbox/Checkbox";

export interface OptionType {
  id: string;
  displayName: string;
  email?: string;
}

interface CustomSelectProps {
  options: OptionType[];
  label?: string;
  placeholder?: string;
  name?: string;
  defaultValue?: string | string[];
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  // onCheckBoxChange?: (e: React.ChangeEvent) => void;
  startIcon?: React.ReactNode;
  error?: string;
  disabled?: boolean;
  title?: string;
  bottom?: string;
  endIcon?: React.ReactNode;
  handleStartIcon?: () => void;
  shouldShowCheckbox?: boolean;
  exclusiveOptionId?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  options,
  label,
  placeholder = "Select",
  name,
  defaultValue,
  onChange,
  //onCheckBoxChange,
  startIcon,
  error,
  disabled,
  title,
  bottom,
  endIcon,
  handleStartIcon,
  shouldShowCheckbox,
  exclusiveOptionId,
}) => {
  const inputId = name || `input-${label}`;
  const errorId = `${inputId}-error`;

  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>();

  useClickOutside(dropdownRef, () => setIsOpen(false));

  const isMultiSelect = Array.isArray(defaultValue);
  const selectedIds = isMultiSelect
    ? (defaultValue as string[]).map(String)
    : defaultValue
      ? [String(defaultValue)]
      : [];

  const selectedOptions = options.filter((opt) =>
    selectedIds.includes(String(opt.id)),
  );
  const selectedDisplayText = selectedOptions
    .map((option) => option.displayName)
    .join(", ");
  const hasExclusiveOptionSelected = Boolean(
    exclusiveOptionId && selectedIds.includes(exclusiveOptionId),
  );
  const hasValidOptions =
    options &&
    options.some((opt) => opt.displayName && opt.displayName.trim() !== "");

  const updateDropdownPosition = () => {
    const select = dropdownRef.current;
    const trigger = select?.querySelector(".custom-select-wrapper");
    if (!select || !trigger) return;

    const triggerRect = trigger.getBoundingClientRect();
    const viewportPadding = 8;
    const gap = 4;
    const preferredMaxHeight = 320;
    const minScrollableHeight = 80;
    const spaceBelow =
      window.innerHeight - triggerRect.bottom - gap - viewportPadding;
    const spaceAbove = triggerRect.top - gap - viewportPadding;
    const shouldDropUp =
      spaceBelow < preferredMaxHeight && spaceAbove > spaceBelow;
    const availableSpace = shouldDropUp ? spaceAbove : spaceBelow;
    const maxHeight = Math.max(
      minScrollableHeight,
      Math.min(preferredMaxHeight, availableSpace),
    );
    const width = Math.max(triggerRect.width, 160);
    const left = Math.max(
      viewportPadding,
      Math.min(
        triggerRect.left,
        window.innerWidth - width - viewportPadding,
      ),
    );

    setDropUp(shouldDropUp);
    setDropdownStyle({
      position: "fixed",
      left,
      width,
      maxHeight,
      marginTop: 0,
      marginBottom: 0,
      zIndex: 9999,
      ...(shouldDropUp
        ? {
            top: "auto",
            bottom: window.innerHeight - triggerRect.top + gap,
          }
        : {
            top: triggerRect.bottom + gap,
            bottom: "auto",
          }),
    });
  };

  useLayoutEffect(() => {
    if (!isOpen) return;

    let animationFrameId: number | null = null;

    const schedulePositionUpdate = () => {
      if (animationFrameId !== null) return;
      animationFrameId = window.requestAnimationFrame(() => {
        animationFrameId = null;
        updateDropdownPosition();
      });
    };

    updateDropdownPosition();
    window.addEventListener("resize", schedulePositionUpdate);
    document.addEventListener("scroll", schedulePositionUpdate, true);

    return () => {
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
      window.removeEventListener("resize", schedulePositionUpdate);
      document.removeEventListener("scroll", schedulePositionUpdate, true);
    };
  }, [isOpen, options]);

  const toggleDropdown = () => {
    if (disabled) return;

    setIsOpen((prev) => !prev);
  };

  const handleSelect = (optionId: string) => {
    if (disabled) return;

    const normalizedOptionId = String(optionId);
    let value: string | string[];

    if (isMultiSelect) {
      if (exclusiveOptionId) {
        if (normalizedOptionId === exclusiveOptionId) {
          value = selectedIds.includes(exclusiveOptionId)
            ? []
            : [exclusiveOptionId];
        } else if (selectedIds.includes(exclusiveOptionId)) {
          return;
        } else {
          const selected = new Set(selectedIds);
          if (selected.has(normalizedOptionId)) {
            selected.delete(normalizedOptionId);
          } else {
            selected.add(normalizedOptionId);
          }
          value = Array.from(selected);
        }
      } else {
        const selected = new Set(selectedIds);
        if (selected.has(normalizedOptionId)) {
          selected.delete(normalizedOptionId);
        } else {
          selected.add(normalizedOptionId);
        }
        value = Array.from(selected);
      }
    } else {
      value = normalizedOptionId;
      setIsOpen(false);
    }

    onChange({
      target: { name, value },
    } as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  const handleStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleStartIcon?.();
    setIsOpen(false);
  };

  return (
    <div
      className={`custom-select ${dropUp ? "dropup" : ""}`}
      ref={dropdownRef}
    >
      {label && (
        <p
          className="select-label"
          onClick={() => setIsOpen(false)}
          id={`${inputId}-label`} // unique ID
          role="label"
        >
          {label}
        </p>
      )}

      <div
        className={`custom-select-wrapper ${
          disabled ? "custom_select_disable" : "custom-select__selected"
        }`}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={`${name}-listbox`}
        aria-label={label || placeholder}
        aria-multiselectable={isMultiSelect}
        tabIndex={disabled ? -1 : 0}
        onClick={toggleDropdown}
        onKeyDown={(e) => {
          if (disabled) return;

          switch (e.key) {
            case "Enter":
            case " ":
              e.preventDefault();
              toggleDropdown();
              break;

            case "Escape":
              setIsOpen(false);
              break;

            case "ArrowDown":
              updateDropdownPosition();
              setIsOpen(true);
              break;
          }
        }}
      >
        <div className="select-left">
          {startIcon && <span className="select-start-icon">{startIcon}</span>}

          <div className="custom-select_select-chips">
            {isMultiSelect ? (
              selectedOptions.length > 0 ? (
                <span
                  className="custom-select_multi-value"
                  title={selectedDisplayText}
                >
                  {selectedDisplayText}
                </span>
              ) : (
                <span className="select-placeholder">{placeholder}</span>
              )
            ) : (
              <div className="custom-select_single-value">
                <span className="select-placeholder">
                  {selectedOptions[0]?.displayName || placeholder}
                </span>
                {endIcon && (
                  <span className="select-endIcon" onClick={handleStart}>
                    {endIcon}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <span className={`arrow ${isOpen ? "open" : ""}`}>&#9662;</span>
      </div>

      {isOpen && (
        <ul
          className={`custom-select__options`}
          role="listbox"
          aria-multiselectable={isMultiSelect}
          id={`${name}-listbox`}
          style={dropdownStyle ?? { bottom: bottom }}
        >
          {hasValidOptions ? (
            options.map((option) => {
              const optionId = String(option.id);
              const isSelected = selectedIds.includes(optionId);
              const isOptionDisabled =
                hasExclusiveOptionSelected && optionId !== exclusiveOptionId;

              return (
                <li
                  key={option.id}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={isOptionDisabled}
                  tabIndex={isOptionDisabled ? -1 : 0}
                  className={`custom-select__option ${
                    shouldShowCheckbox
                      ? "custom-select__inputOptions"
                      : isSelected
                        ? "selected"
                        : ""
                  }`}
                  onClick={(e) => {
                    if (isOptionDisabled) return;
                    if (!shouldShowCheckbox) {
                      handleSelect(option.id);
                    }
                    // if ((e.target as HTMLElement).closest(".custom-checkbox"))
                    //   return;
                  }}
                  onKeyDown={(e) => {
                    if (isOptionDisabled) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleSelect(option.id);
                    }
                  }}
                  aria-invalid={error ? "true" : "false"}
                  aria-describedby={error ? errorId : undefined}
                >
                  {shouldShowCheckbox && (
                    <Checkbox
                      data={options}
                      name={name}
                      value={optionId}
                      checked={isSelected}
                      disabled={isOptionDisabled}
                      onChange={(e) => {
                        e.stopPropagation(); // prevent li click
                        handleSelect(optionId);
                      }}
                    />
                  )}
                  <span className="custom-select__option-content">
                    <span>{option.displayName}</span>
                    {option.email && (
                      <span className="custom-select__option-email">
                        {option.email}
                      </span>
                    )}
                  </span>
                </li>
              );
            })
          ) : (
            <li
              className="custom-select__option disabled"
              role="option"
              aria-disabled="true"
              tabIndex={-1}
            >
              {title ? title : "No data available!"}
            </li>
          )}
        </ul>
      )}

      {error && (
        <p
          id={errorId}
          className="error-text"
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default CustomSelect;
